import type { ReactNode } from 'react';
import { Node, mergeAttributes, type Extensions } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { TaskList } from '@tiptap/extension-task-list';
import { TaskItem } from '@tiptap/extension-task-item';
import { TableKit } from '@tiptap/extension-table';
import { TextStyle, Color } from '@tiptap/extension-text-style';
import { Highlight } from '@tiptap/extension-highlight';
import { TextAlign } from '@tiptap/extension-text-align';
import { Mention } from '@tiptap/extension-mention';

import { CodeBlock } from './codeBlock';
import { ResizableImage } from './resizableImage';
import { slashCommand } from './suggestion';
import { mentionSuggestion, type MentionItem } from './suggestion';
import { PageEmbed, type EditorPageContent, type PageProvider } from './pageEmbed';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    callout: { toggleCallout: () => ReturnType };
    video: { setVideo: (options: { src: string }) => ReturnType };
  }
}

/** Normalise common video URLs to an embeddable form (YouTube/Vimeo). */
export function toEmbedUrl(raw: string): string {
  const url = raw.trim();
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}`;
  return url;
}

/** Embedded video (iframe) block. */
export const Video = Node.create({
  name: 'video',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return { src: { default: null } };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="video"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    const src = (HTMLAttributes as { src?: string }).src ?? '';
    return [
      'div',
      { 'data-type': 'video' },
      ['iframe', { src, frameborder: '0', allow: 'fullscreen; picture-in-picture', allowfullscreen: 'true' }],
    ];
  },

  addCommands() {
    return {
      setVideo:
        (options) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs: { src: toEmbedUrl(options.src) } }),
    };
  },
});

/** A coloured "WHAT / HOW" note block (the emoji callout from the spec). */
export const Callout = Node.create({
  name: 'callout',
  group: 'block',
  content: 'block+',
  defining: true,

  addAttributes() {
    return {
      emoji: {
        default: '💡',
        parseHTML: (el) => el.getAttribute('data-emoji') || '💡',
        renderHTML: (attrs) => ({ 'data-emoji': attrs.emoji }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="callout"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'callout' }), 0];
  },

  addCommands() {
    return {
      toggleCallout:
        () =>
        ({ commands }) =>
          commands.toggleWrap(this.name),
    };
  },
});

/** The full block-editor extension set (ADR-074 / FE-ADR-054). The optional
 *  `pageProvider` enables the "/page" embed block (FE-ADR-055); `renderContent`
 *  lets the embed render a stored page document read-only (passed by the host
 *  so block-editor avoids a circular import on itself). */
export function buildExtensions(opts: {
  placeholder?: string;
  members?: MentionItem[];
  pageProvider?: PageProvider;
  renderContent?: (page: EditorPageContent) => ReactNode;
}): Extensions {
  return [
    StarterKit.configure({
      heading: { levels: [1, 2, 3, 4, 5, 6] },
      codeBlock: false, // replaced by CodeBlockLowlight (syntax highlight)
      link: { openOnClick: false, autolink: true },
    }),
    Placeholder.configure({
      // Suppress the placeholder inside code blocks — the hint is meaningless
      // there, and its floated ::before renders on the code block's NodeView
      // wrapper (on top of the language-picker header bar). Empty string → the
      // `content: attr(data-placeholder)` pseudo-element renders nothing.
      placeholder: ({ node }) =>
        node.type.name === 'codeBlock' ? '' : (opts.placeholder ?? 'Type “/” for commands…'),
      includeChildren: true,
    }),
    TaskList,
    TaskItem.configure({ nested: true }),
    TableKit.configure({
      table: { resizable: true, lastColumnResizable: true, cellMinWidth: 48 },
    }),
    ResizableImage.configure({ inline: false, allowBase64: true }),
    TextStyle,
    Color,
    Highlight.configure({ multicolor: true }),
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
    CodeBlock,
    Callout,
    Video,
    PageEmbed.configure({ provider: opts.pageProvider, renderContent: opts.renderContent }),
    slashCommand({ pageProvider: opts.pageProvider }),
    ...(opts.members && opts.members.length
      ? [Mention.configure({ suggestion: mentionSuggestion(opts.members) })]
      : []),
  ];
}

/** Compact preset for work-item descriptions (FE-ADR-054): basic formatting +
 *  checklists + code + images, driven entirely by a slim toolbar. Deliberately
 *  drops the slash menu, tables, callouts, video, colour/highlight/align,
 *  mentions and page-embeds — a task note, not a document. */
export function buildCompactExtensions(opts: { placeholder?: string }): Extensions {
  return [
    StarterKit.configure({
      heading: { levels: [1, 2, 3] },
      codeBlock: false, // replaced by CodeBlock (syntax highlight)
      link: { openOnClick: false, autolink: true },
    }),
    Placeholder.configure({
      placeholder: ({ node }) =>
        node.type.name === 'codeBlock' ? '' : (opts.placeholder ?? 'Write a description…'),
      includeChildren: true,
    }),
    TaskList,
    TaskItem.configure({ nested: true }),
    ResizableImage.configure({ inline: false, allowBase64: true }),
    CodeBlock,
  ];
}
